"use client";

import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  IconButton,
  CircularProgress,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

import CustomButton from "./CustomButton";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";

interface Tone {
  id: number;
  rbtName: string;
  artistName: string;
  playPath: string;
  rbtCode: string;
}

interface StoreDetailPageProps {
  tones: Tone[];
}

const ITEMS_PER_PAGE = 10;

const StoreDetailPage: React.FC<StoreDetailPageProps> = ({ tones }) => {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const audioRef = useRef<HTMLAudioElement>(new Audio());

  const { countryCode, msisdn } = useSelector(
    (state: RootState) => state.phone
  );

  const queryParams = new URLSearchParams(location.search);
  const catId = queryParams.get("catId");

const fetchAudioAndPlay = async (playPath: string, id: string) => {
  try {
    const username = countryCode + msisdn;
    const baseUrl = process.env.REACT_APP_API_URL;

    const url = `${baseUrl}/Content/handleMusicPlayer?msisdn=${username}&service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}&playPath=${playPath}`;

    const response = await axios.get(url, {
      responseType: "blob",
    });

    const audioBlob = new Blob([response.data], { type: "audio/wav" });
    const audioURL = URL.createObjectURL(audioBlob);

    audioRef.current.pause();

    audioRef.current.src = audioURL;

    await audioRef.current.play();

    setPlayingId(id);

  } catch (error) {
    console.error("Audio fetch/play error:", error);
    alert("Unable to play audio");
  }
};


const handlePlayPause = (tone: Tone) => {
  if (playingId === tone.rbtCode) {
    audioRef.current.pause();
    setPlayingId(null);
  } else {
    fetchAudioAndPlay(tone.playPath, tone.rbtCode);
  }
};


  const handleLoadMore = (e: React.MouseEvent) => {
    e.preventDefault();
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  React.useEffect(() => {
    const audio = audioRef.current;

    audio.onended = () => setPlayingId(null);

    return () => {
      audio.onended = null;
    };
  }, []);

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Card
        sx={{
          border: "1px solid #ccc",
          borderRadius: 2,
          p: 2,
          backgroundColor: "#fff",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#0085ca" }}>
              <TableCell sx={{ width: "15%" }}></TableCell>
              <TableCell
                sx={{
                  width: "45%",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: "bold",
                }}
              >
                Song
              </TableCell>
              <TableCell sx={{ width: "20%" }}></TableCell>
              <TableCell
                sx={{
                  width: "20%",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: "bold",
                }}
              >
                Buy
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {tones.slice(0, visibleCount).map((tone) => (
              <TableRow
                key={tone.id}
                sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
              >
                {/* Play Button */}
                <TableCell>
                  <IconButton
                    onClick={() => handlePlayPause(tone)}
                    sx={{ border: "1px solid #ccc" }}
                  >
                    {playingId === tone?.rbtCode ? (
                      <PauseIcon />
                    ) : (
                      <PlayArrowIcon />
                    )}
                  </IconButton>
                </TableCell>

                {/* Song Name + Artist */}
                <TableCell sx={{ color: "#0085ca", fontSize: 16 }}>
                  {tone.rbtName}
                  <br />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 13, color: "gray" }}
                  >
                    {tone.artistName}
                  </Typography>
                </TableCell>

                <TableCell></TableCell>

                {/* Buy / Gift */}
                <TableCell>
                  <CustomButton
                    text="Buy / Gift"
                    sx={{ width: 114, py: "5px !important" }}
                    onClick={() =>
                      navigate(
                        `/flowtone/detalle?rbtCode=${tone.rbtCode}&catId=${catId}`,
                        { state: tone }
                      )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {visibleCount < tones.length && (
          <Box sx={{ textAlign: "right", mt: 2 }}>
            <Typography
              component="a"
              href="#"
              onClick={handleLoadMore}
              sx={{
                color: "#0085ca",
                fontSize: 16,
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                cursor: "pointer",
              }}
            >
              More &gt;&gt;
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default StoreDetailPage;
